import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Search, CheckCircle, XCircle, Calendar, Globe, Award, Shield, User, Clock, Layers } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerificationPage() {
  const [searchParams] = useSearchParams();
  const [searchForm, setSearchForm] = useState({ certificate_number: '', candidate_name: '', date_of_birth: '' });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Auto-search if cert parameter is provided (QR code scan)
  useEffect(() => {
    const certParam = searchParams.get('cert');
    if (certParam) {
      setSearchForm(prev => ({ ...prev, certificate_number: certParam }));
      handleQRSearch(certParam);
    }
  }, [searchParams]);

  const handleQRSearch = async (certNumber) => {
    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.get(`${API}/certificates/verify?cert=${encodeURIComponent(certNumber)}`);
      // API returns { verified: bool, certificate: obj } or { verified: false, message: str }
      if (res.data.verified && res.data.certificate) {
        setResults([res.data.certificate]);
      } else {
        setResults([]);
      }
    } catch (err) {
      setResults([]);
    } finally { setLoading(false); }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchForm.certificate_number && !searchForm.candidate_name && !searchForm.date_of_birth) return;
    setLoading(true);
    setSearched(true);
    try {
      const params = new URLSearchParams();
      if (searchForm.certificate_number) params.append('certificate_number', searchForm.certificate_number);
      if (searchForm.candidate_name) params.append('candidate_name', searchForm.candidate_name);
      if (searchForm.date_of_birth) params.append('date_of_birth', searchForm.date_of_birth);
      const res = await axios.get(`${API}/certificates/verify?${params.toString()}`);
      // API returns { verified: bool, certificate: obj } or { verified: false, message: str }
      if (res.data.verified && res.data.certificate) {
        setResults([res.data.certificate]);
      } else {
        setResults([]);
      }
    } catch (err) {
      setResults([]);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="verification-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_cbeam-portal/artifacts/6xytjpjs_IMCAC%20%287%29.png"
            alt="IMCAC"
            className="h-14 w-14 mx-auto mb-4 rounded-sm"
          />
          <p className="imcac-overline mb-3">Public Verification</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Certificate Verification Portal</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5 mx-auto" />
          <p className="text-lg text-slate-300 mt-4 max-w-xl mx-auto">
            Verify the authenticity and status of IMCAC certifications
          </p>
        </div>
      </section>

      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container max-w-3xl">
          <Card className="imcac-card mb-8" data-testid="verification-search-form">
            <CardContent className="p-6 md:p-8">
              <h2 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4">Search by any combination</h2>
              <form onSubmit={handleSearch} className="space-y-4">
                <div>
                  <Label className="text-sm">Certificate Number</Label>
                  <Input
                    value={searchForm.certificate_number}
                    onChange={e => setSearchForm({...searchForm, certificate_number: e.target.value})}
                    placeholder="e.g., IMCAC-CBEAM-2024-001"
                    className="rounded-sm mt-1"
                    data-testid="verify-cert-number"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Candidate Name</Label>
                    <Input
                      value={searchForm.candidate_name}
                      onChange={e => setSearchForm({...searchForm, candidate_name: e.target.value})}
                      placeholder="e.g., Dr. Sarah Johnson"
                      className="rounded-sm mt-1"
                      data-testid="verify-name"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Date of Birth</Label>
                    <Input
                      type="date"
                      value={searchForm.date_of_birth}
                      onChange={e => setSearchForm({...searchForm, date_of_birth: e.target.value})}
                      className="rounded-sm mt-1"
                      data-testid="verify-dob"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm py-3" data-testid="verify-search-btn">
                  <Search className="h-4 w-4 mr-2" /> {loading ? 'Searching...' : 'Verify Certificate'}
                </Button>
              </form>
              <p className="text-xs text-slate-500 mt-3 text-center">
                Try: IMCAC-CBEAM-2024-001, Dr. Sarah Johnson, or Dr. Ahmed Hassan
              </p>
            </CardContent>
          </Card>

          {/* Results */}
          {searched && results !== null && (
            <div data-testid="verification-results">
              {results.length === 0 ? (
                <Card className="imcac-card">
                  <CardContent className="p-8 text-center">
                    <XCircle className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-2">No Results Found</h3>
                    <p className="text-sm text-slate-500">No certificates match your search criteria. Please verify the information and try again.</p>
                  </CardContent>
                </Card>
              ) : (
                results.map((cert) => (
                  <Card key={cert.id || cert.certificate_number} className="imcac-card mb-4 border-2 border-green-200" data-testid={`cert-result-${cert.certificate_number}`}>
                    <CardContent className="p-6 md:p-8">
                      {/* Verification Banner */}
                      <div className="bg-green-50 border border-green-200 rounded-sm p-4 mb-6 flex items-center gap-3">
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <Shield className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-green-800">VERIFIED CERTIFICATE</p>
                          <p className="text-xs text-green-600">This certificate has been verified by IMCAC</p>
                          <p className="text-xs text-green-500 flex items-center gap-1 mt-1">
                            <Clock className="h-3 w-3" /> Verified at: {new Date().toLocaleString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start gap-4">
                          {/* Candidate Photo */}
                          {cert.candidate_photo_url ? (
                            <img 
                              src={`${process.env.REACT_APP_BACKEND_URL}${cert.candidate_photo_url}`}
                              alt={cert.candidate_name || cert.full_name}
                              className="w-20 h-20 object-cover rounded-sm border-2 border-[#C5A572]"
                            />
                          ) : (
                            <div className="w-20 h-20 bg-slate-100 rounded-sm border-2 border-slate-200 flex items-center justify-center">
                              <User className="h-8 w-8 text-slate-400" />
                            </div>
                          )}
                          <div>
                            <p className="text-xs font-bold uppercase tracking-widest text-[#C5A572] mb-1">Certified Professional</p>
                            <h3 className="text-xl font-bold text-[#1B2A4A] font-heading">{cert.candidate_name || cert.full_name}</h3>
                            <p className="text-sm text-slate-500 font-mono">{cert.certificate_number}</p>
                          </div>
                        </div>
                        <Badge className={`rounded-sm px-3 py-1 ${
                          (cert.status === 'valid' || cert.validity_status === 'Active') ? 'bg-green-100 text-green-700' : 
                          (cert.status === 'suspended' || cert.validity_status === 'Suspended') ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`} data-testid="cert-validity">
                          <CheckCircle className="h-3 w-3 mr-1" /> {cert.validity_status || (cert.status === 'valid' ? 'Active' : cert.status?.charAt(0).toUpperCase() + cert.status?.slice(1) || 'Active')}
                        </Badge>
                      </div>
                      <Separator className="mb-4" />
                      
                      {/* Certificate Details Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {[
                          { icon: Award, label: 'Qualification', value: cert.certification_title || cert.qualification },
                          { icon: Layers, label: 'IPCF Level', value: cert.ipcf_level, testId: 'cert-ipcf-level' },
                          { icon: Globe, label: 'Classification', value: cert.classification || 'Certified' },
                          { icon: Shield, label: 'Certification Type', value: cert.certification_type || 'Board Certification' },
                          { icon: Calendar, label: 'Route Completed', value: cert.route || cert.route_completed },
                          { icon: Calendar, label: 'Issue Date', value: cert.issue_date || cert.exam_date },
                          { icon: Calendar, label: 'Valid Until', value: cert.expiry_date || cert.recertification_due },
                        ].map((item, i) => (
                          <div key={i} className="p-3 bg-[#F8FAFC] rounded-sm" data-testid={item.testId}>
                            <p className="text-xs text-slate-500 flex items-center gap-1"><item.icon className="h-3 w-3" /> {item.label}</p>
                            <p className="text-sm font-medium text-[#1B2A4A] mt-0.5">{item.value || '-'}</p>
                          </div>
                        ))}
                      </div>
                      
                      {cert.cme_summary && (
                        <div className="mt-4 p-3 bg-[#F8FAFC] rounded-sm">
                          <p className="text-xs text-slate-500">CPD Record</p>
                          <p className="text-sm font-medium text-[#1B2A4A]">{cert.cme_summary}</p>
                        </div>
                      )}

                      {/* Authenticity Watermark */}
                      <div className="mt-6 p-4 bg-gradient-to-r from-[#1B2A4A]/5 to-[#C5A572]/5 rounded-sm border border-dashed border-[#C5A572]/30">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img
                              src="https://customer-assets.emergentagent.com/job_cbeam-portal/artifacts/6xytjpjs_IMCAC%20%287%29.png"
                              alt="IMCAC"
                              className="h-8 w-8"
                            />
                            <div>
                              <p className="text-xs font-bold text-[#1B2A4A]">IMCAC Verified</p>
                              <p className="text-[10px] text-slate-500">International Medical Competency Accreditation Council</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-[10px] text-slate-400">Certificate Number</p>
                            <p className="text-xs font-mono text-slate-500">{cert.certificate_number || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
